// Çalışma İzni Wizard - Tam Sayfa
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { KvkkRenewalBanner } from '../components/firm/KvkkRenewalBanner';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import WorkPermitWizard from '../components/work-permits/WorkPermitWizard';
import { useAuth } from '../context/AuthContext';
import { StoreQuotaGate } from '../components/stores/StoreQuotaGate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WorkPermitWizardPage = () => {
  const outlet = useOutletContext();
  const { permitId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(!!permitId);
  const [existingPermit, setExistingPermit] = useState(null);
  
  const isEditMode = !!permitId;
  const { isFirm } = useAuth();
  const reviewing = !isFirm && existingPermit?.status === 'isg_inceleme';

  // İnceleyen sayfadayken kilidi 45 sn.de bir tazele (kapanmayan sekmede kilit 2 dk içinde düşer), çıkınca bırak (firma "İSG kontrol ediyor" görür)
  useEffect(() => {
    if (!reviewing) return undefined;
    const send = (active) => fetch(`${API}/work-permits/${permitId}/review-presence`, {
      method: 'PUT', keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ active }),
    }).catch(() => {});
    const start = setTimeout(() => send(true), 500);
    const t = setInterval(() => send(true), 45 * 1000);
    const release = () => send(false);
    window.addEventListener('pagehide', release);
    return () => { clearTimeout(start); clearInterval(t); window.removeEventListener('pagehide', release); release(); };
  }, [reviewing, permitId]);
  
  useEffect(() => {
    if (permitId) {
      loadPermit();
    }
  }, [permitId]);
  
  const loadPermit = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/work-permits/${permitId}`);
      setExistingPermit(res.data);
    } catch (error) {
      console.error('Permit load error:', error);
      navigate('/work-permits');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSuccess = () => {
    navigate('/work-permits');
  };
  
  const handleClose = () => {
    navigate('/work-permits');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Yükleniyor...</span>
        </div>
      </div>
    );
  }
  
  if (!isEditMode && outlet?.myCompany?.kvkk_renewal_required) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 space-y-4" data-testid="wizard-kvkk-locked">
        <Button variant="ghost" onClick={handleClose}><ArrowLeft className="h-4 w-4 mr-2" />Çalışma İzinlerine Dön</Button>
        <KvkkRenewalBanner company={outlet.myCompany} onChanged={outlet.reloadMyCompany} />
      </div>
    );
  }

  if (isEditMode && !existingPermit) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-600">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Talep yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <StoreQuotaGate enabled={isFirm && !isEditMode} />
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="px-2 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center gap-2 sm:gap-4 flex-nowrap">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleClose}
              data-leave-target="/work-permits"
              className="flex items-center gap-2"
              data-testid="wizard-page-back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              Geri
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <h1 className="text-base sm:text-lg font-semibold text-slate-800 truncate">
              {isEditMode ? `Çalışma İzni Düzenle - #${existingPermit?.request_number}` : 'Yeni Çalışma İzni'}
            </h1>
          </div>
        </div>
      </div>
      
      {/* Wizard Content - Full Width */}
      <div className="px-0 sm:px-4 py-3 sm:py-4">
        <div className="bg-white sm:rounded-lg sm:shadow-sm sm:border p-0 sm:p-4">
          <WorkPermitWizard
            existingPermit={existingPermit}
            editMode={isEditMode}
            onSuccess={handleSuccess}
            onClose={handleClose}
            isFullPage={true}
          />
        </div>
      </div>
    </div>
  );
};

export default WorkPermitWizardPage;
